/**
 * Forgetting a password, and setting a new one from the mailed link (review F5).
 *
 * The link is `/reset-password#<token>`: the token rides after the hash, which a
 * browser never sends to any server, so it cannot land in an access log or a
 * `Referer`. The page lifts it out of the address bar as it opens, so it is not
 * left in the history either.
 *
 * Like the Notify pages, nothing happens on arrival. A mail scanner that follows
 * the link sees a form; only somebody who types a new password spends it.
 */

import { type FormEvent, useId, useState } from "react";
import { BrandMark } from "@/components/ui/BrandMark";
import { Button, FieldShell, TextInput } from "@/components/ui/controls";
import { Notice } from "@/components/ui/states";
import { ApiError, api } from "@/lib/api";

export const MIN_LENGTH = 12;

/** What to say when a call fails: the server's words, or that it was not reached. */
export function failure(cause: unknown): string {
  return cause instanceof ApiError
    ? cause.message
    : "Could not reach the server. Check your connection and try again.";
}

/** Inside the sign-in card, in place of the password form. */
export function ForgotPassword({ onBack }: { onBack: () => void }) {
  const emailId = useId();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      setSent((await api.forgotPassword(email)).message);
    } catch (cause) {
      setError(failure(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="panel grid gap-4 rounded-panel p-5">
      {sent ? (
        <Notice>{sent}</Notice>
      ) : (
        <>
          {error && <Notice tone="danger">{error}</Notice>}
          <FieldShell
            label="Email"
            htmlFor={emailId}
            required
            help="We will send a link to set a new password."
          >
            <TextInput
              id={emailId}
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </FieldShell>
          <Button type="submit" variant="primary" pending={pending} pendingLabel="Sending…">
            Send the link
          </Button>
        </>
      )}
      <Button type="button" variant="ghost" onClick={onBack}>
        Back to sign in
      </Button>
    </form>
  );
}

/** Read once, then gone from the address bar. */
export function takeToken(): string {
  const token = window.location.hash.slice(1);
  if (token) window.history.replaceState(null, "", window.location.pathname);
  return token;
}

export function ResetPasswordPage() {
  const passwordId = useId();
  const repeatId = useId();
  const [token] = useState(takeToken);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (password.length < MIN_LENGTH) {
      setProblem(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== repeat) {
      setProblem("The two passwords are not the same.");
      return;
    }
    setProblem(null);
    setPending(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (cause) {
      setProblem(failure(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center px-4 py-12">
      <div className="w-full max-w-sm">
        <header className="mb-6">
          <p className="mb-5 flex items-center gap-2">
            <BrandMark size="size-7" />
            <span className="label-tiny">Future Minds CMS</span>
          </p>
          <h1 className="text-display font-semibold tracking-tight text-ink">
            {done ? "Password changed" : "Set a new password"}
          </h1>
          <p className="mt-0.5 text-md text-ink-muted">
            {done
              ? "Every device that was signed in has been signed out."
              : "Choose a password you do not use anywhere else."}
          </p>
        </header>

        {done ? (
          <a
            href="/"
            className="panel block rounded-panel p-5 text-center font-medium text-accent"
          >
            Sign in with your new password
          </a>
        ) : !token ? (
          <Notice tone="danger">
            This page needs the link from the email. Ask for a new one from the sign-in page.
          </Notice>
        ) : (
          <form onSubmit={onSubmit} noValidate className="panel grid gap-4 rounded-panel p-5">
            {problem && <Notice tone="danger">{problem}</Notice>}
            <FieldShell
              label="New password"
              htmlFor={passwordId}
              required
              help={`At least ${MIN_LENGTH} characters.`}
            >
              <TextInput
                id={passwordId}
                type="password"
                autoComplete="new-password"
                autoFocus
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </FieldShell>
            <FieldShell label="The same again" htmlFor={repeatId} required>
              <TextInput
                id={repeatId}
                type="password"
                autoComplete="new-password"
                required
                value={repeat}
                onChange={(event) => setRepeat(event.target.value)}
              />
            </FieldShell>
            <Button type="submit" variant="primary" pending={pending} pendingLabel="Saving…">
              Set the password
            </Button>
          </form>
        )}
      </div>
    </main>
  );
}
